import type {
  EnhancedScopeData,
  LoadedGraphWithViewpointMode,
} from '@ardoq/data-model';
import type { ArdoqId, GroupingRule } from '@ardoq/api-types';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import { isTruthy } from '@ardoq/common-helpers';
import type { BlocksNode } from 'tabview/visualizations/aq-blocks/types';
import { buildBlocksGraph } from 'tabview/assistantBlocks/buildBlocksGraph';
import {
  effectiveArrangements,
  resolveContainerArrangements,
} from 'tabview/assistantBlocks/resolveArrangements';
import type { LabelFormattingFilter } from 'tabview/assistantShared/buildBlocksGraph';
import type { ModelIdStyle } from 'tabview/assistantShared/buildStyleByModelId';
import type {
  BlocksArrangement,
  BlocksRegion,
} from 'tabview/assistantBlocks/arrangementRecipe';
import {
  type ArrangementOutcome,
  type ResolvedArrangement,
  parseTemplateAreas,
  summarizeArrangement,
} from 'tabview/assistantBlocks/compileArrangement';
import {
  type HierarchyRoot,
  type ReferenceTypeCount,
  countSiblingReferenceTypes,
  findNode,
  toArrangeableNodes,
  toHierarchyRoots,
} from 'tabview/assistantBlocks/arrangeableNodes';
import { getComponentTypeName } from 'tabview/assistantBlocks/componentTypeName';
import {
  type ManualTabularNode,
  toGlobalCells,
} from 'tabview/assistantBlocks/manualTabular';
import type { BlocksGraphInputs } from './blocksGraphInputs$';

// The AI context only needs node structure (describeContainers ignores color and representation).
const NO_COMPONENT_STYLES = new Map<ArdoqId, ModelIdStyle>();
const NO_LABEL_RULES: LabelFormattingFilter[] = [];
const NO_USER_FIELD_FORMATTING = () => null;

const MAX_DESCRIBED_CONTAINERS = 40;
const MAX_LISTED_LABELS = 30;

type ComponentTypeCount = { name: string; count: number };
type BlocksContainerDescriptor = {
  id: string | null;
  label: string;
  parentId: string | null;
  childCount: number;
  childComponentTypes: ComponentTypeCount[];
  /** Component types anywhere inside, at any depth, with counts. */
  descendantComponentTypes: ComponentTypeCount[];
  /** Reference types connecting these children to each other, with how many references. */
  childReferenceTypes: ReferenceTypeCount[];
  childLabels: LabelledChild[];
  omittedLabelCount: number;
  hierarchyRoots: HierarchyRoot[];
};

type AppliedArrangement = {
  containerId: string | null;
  areas: string[];
  regions: BlocksRegion[];
  outcome: ArrangementOutcome;
};

/** The whole layout's size in node tracks, which are roughly square blocks. */
type LayoutSize = {
  blocksAcross: number;
  blocksDown: number;
  /** blocksAcross / blocksDown: above 1 is landscape. */
  aspectRatio: number;
};

export type BlocksContext = {
  containers: BlocksContainerDescriptor[];
  /** Groups left out of `containers` to keep the context small, the deepest first. */
  omittedContainerCount: number;
  appliedArrangements: AppliedArrangement[];
  layoutSize: LayoutSize;
};

const NODE_TRACKS_PER_PAIR = 2;

const toBlockCount = (span: number): number =>
  Math.max(1, Math.ceil(span / NODE_TRACKS_PER_PAIR));

const measureLayout = (root: BlocksNode): LayoutSize =>
  toLayoutSize(
    root.requestedCell?.colSpan ?? 1,
    root.requestedCell?.rowSpan ?? 1
  );

const toLayoutSize = (colSpan: number, rowSpan: number): LayoutSize => {
  const blocksAcross = toBlockCount(colSpan);
  const blocksDown = toBlockCount(rowSpan);
  return {
    blocksAcross,
    blocksDown,
    aspectRatio: Math.round((blocksAcross / blocksDown) * 100) / 100,
  };
};

const countComponentTypes = (
  scopeData: EnhancedScopeData,
  children: BlocksNode[]
): ComponentTypeCount[] => {
  const counts = children
    .map(child => getComponentTypeName(scopeData, child))
    .filter(isTruthy)
    .reduce(
      (acc, name) => acc.set(name, (acc.get(name) ?? 0) + 1),
      new Map<string, number>()
    );
  return [...counts].map(([name, count]) => ({ name, count }));
};

const descendantsOf = (node: BlocksNode): BlocksNode[] =>
  (node.children ?? []).flatMap(child => [child, ...descendantsOf(child)]);

const byGroupsFirst = (a: BlocksNode, b: BlocksNode): number =>
  Number(Boolean(b.children)) - Number(Boolean(a.children));

type LabelledChild = { label: string; componentType: string | null };

const listLabels = (
  scopeData: EnhancedScopeData,
  children: BlocksNode[]
): LabelledChild[] =>
  [...children]
    .sort(byGroupsFirst)
    .slice(0, MAX_LISTED_LABELS)
    .map(child => ({
      label: child.labels[0] ?? '',
      componentType: getComponentTypeName(scopeData, child),
    }));

const describeContainer = (
  scopeData: EnhancedScopeData,
  node: BlocksNode,
  parentId: string | null,
  isRoot: boolean
): BlocksContainerDescriptor => {
  const children = node.children ?? [];
  const arrangeable = toArrangeableNodes(scopeData, children);
  return {
    id: isRoot ? null : node.id,
    label: isRoot ? '(top level)' : (node.labels[0] ?? ''),
    parentId,
    childCount: children.length,
    childComponentTypes: countComponentTypes(scopeData, children),
    descendantComponentTypes: countComponentTypes(
      scopeData,
      descendantsOf(node)
    ),
    childReferenceTypes: countSiblingReferenceTypes(scopeData, children),
    childLabels: listLabels(scopeData, children),
    omittedLabelCount: Math.max(0, children.length - MAX_LISTED_LABELS),
    hierarchyRoots: toHierarchyRoots(arrangeable),
  };
};

type ContainerVisit = {
  node: BlocksNode;
  parentId: string | null;
};

/** Every group, level by level, so a cap cuts the deepest groups first. */
const groupsByLevel = (root: BlocksNode): ContainerVisit[] => {
  const visit = (level: ContainerVisit[]): ContainerVisit[] => {
    if (level.length === 0) return [];
    const next = level.flatMap(({ node }) =>
      (node.children ?? [])
        .filter(child => child.children)
        .map(child => ({
          node: child,
          parentId: node === root ? null : node.id,
        }))
    );
    return [...level, ...visit(next)];
  };
  return visit([{ node: root, parentId: null }]);
};

const describeContainers = (
  scopeData: EnhancedScopeData,
  root: BlocksNode
): { containers: BlocksContainerDescriptor[]; omitted: number } => {
  const groups = groupsByLevel(root);
  return {
    containers: groups
      .slice(0, MAX_DESCRIBED_CONTAINERS)
      .map(({ node, parentId }) =>
        describeContainer(scopeData, node, parentId, node === root)
      ),
    omitted: Math.max(0, groups.length - MAX_DESCRIBED_CONTAINERS),
  };
};

const toAppliedArrangement = (
  resolved: ResolvedArrangement
): AppliedArrangement => ({
  containerId: resolved.arrangement.containerId,
  areas: resolved.arrangement.areas,
  regions: resolved.arrangement.regions,
  outcome: summarizeArrangement(resolved),
});

/** How the assistant is told which container an arrangement is for. */
export const containerName = (
  root: BlocksNode,
  containerId: string | null
): string =>
  containerId === null
    ? 'the top level'
    : `the group '${findNode(root, containerId)?.labels[0] ?? containerId}'`;

export const buildGroupTree = ({
  loadedGraph,
  groupingRules,
  i18nLocale,
  locale,
}: BlocksGraphInputs): BlocksNode =>
  buildBlocksGraph({
    loadedGraph,
    styleByModelId: NO_COMPONENT_STYLES,
    arrangements: [],
    collapsedGroupIds: [],
    locale,
    groupingRules,
    formattingFilters: NO_LABEL_RULES,
    formatUserFieldFn: NO_USER_FIELD_FORMATTING,
    i18nLocale,
  }).graph.root;

const describeAlignmentProblem = (
  arrangement: AppliedArrangement,
  { name, count, unrelated }: ArrangementOutcome['regions'][number]
): string[] => {
  const alignWith = arrangement.regions.find(
    region => region.name === name
  )?.alignWith;
  if (!alignWith) return [];
  if (unrelated === null) {
    return [
      `Region '${name}' can't align with '${alignWith.region}': that must be another region holding blocks in the same arrangement, and the areas must put '${name}' directly below or above it spanning all its columns, or beside it spanning all its rows.`,
    ];
  }
  if (unrelated < count) return [];
  const types = alignWith.referenceTypes.length
    ? ` by ${alignWith.referenceTypes.join(' or ')}`
    : '';
  return [
    `None of the ${count} blocks in region '${name}' has a reference${types} to a block in region '${alignWith.region}', or to anything inside its groups, so nothing can be lined up.`,
  ];
};

const describeEmptyAlignedRegions = ({
  areas,
  regions,
  outcome,
}: AppliedArrangement): string[] => {
  const areaNames = parseTemplateAreas(areas);
  const held = new Set(outcome.regions.map(({ name }) => name));
  return regions
    .filter(({ name, alignWith }) => alignWith && !held.has(name))
    .map(({ name }) =>
      areaNames.has(name)
        ? `Region '${name}' holds no blocks in this group, so it lines nothing up. An arrangement only places the group's own children. Arrange the level that holds those blocks instead: there, a reference to a block nested inside a group lines up with that group.`
        : `Region '${name}' has no area in 'areas', so it places nothing. Name it exactly as an area in the template, or add an area for it.`
    );
};

/**
 * Why any region of these arrangements that sets alignWith would line nothing up: one whose
 * alignWith can't apply, or whose blocks relate to none in the region it names.
 */
export const describeAlignmentProblems = (
  root: BlocksNode,
  resolved: ResolvedArrangement[]
): string[] =>
  resolved.map(toAppliedArrangement).flatMap(arrangement => {
    const container = containerName(root, arrangement.containerId);
    return [
      ...describeEmptyAlignedRegions(arrangement),
      ...arrangement.outcome.regions.flatMap(region =>
        describeAlignmentProblem(arrangement, region)
      ),
    ].map(problem => `In the arrangement for ${container}: ${problem}`);
  });

/** Interactive Blocks' saved cells, keyed by parent and node id, on its odd per-group tracks. */
export type StoredCells = Record<
  string,
  { col: number; row: number; colSpan: number; rowSpan: number; open?: boolean }
>;

export type BlocksContextArgs = {
  loadedGraph: LoadedGraphWithViewpointMode;
  groupingRules: GroupingRule[];
  i18nLocale: I18nLocale;
  locale: Locale;
  arrangements: BlocksArrangement[];
  /** The layout the user sees when it is stored per node rather than compiled from the recipe. */
  storedLayout: StoredCells | null;
};

const storedKey = (root: BlocksNode, parent: BlocksNode, node: BlocksNode) =>
  parent === root ? node.id : `${parent.id}-${node.id}`;

const isUsableCell = (cell: StoredCells[string] | undefined): boolean =>
  Boolean(cell) &&
  [cell!.col, cell!.row, cell!.colSpan, cell!.rowSpan].every(Number.isFinite);

const toStoredTree = (
  root: BlocksNode,
  stored: StoredCells,
  node: BlocksNode,
  parent: BlocksNode | null
): ManualTabularNode<BlocksNode> => {
  const cell = parent ? stored[storedKey(root, parent, node)] : undefined;
  const isOpen = cell?.open !== false;
  return {
    node,
    cell:
      cell && isUsableCell(cell)
        ? {
            col: (cell.col - 1) / 2,
            row: (cell.row - 1) / 2,
            colSpan: (cell.colSpan + 1) / 2,
            rowSpan: (cell.rowSpan + 1) / 2,
          }
        : undefined,
    children: isOpen
      ? (node.children ?? []).map(child =>
          toStoredTree(root, stored, child, node)
        )
      : [],
  };
};

const measureStoredLayout = (
  root: BlocksNode,
  stored: StoredCells
): LayoutSize => {
  const rootCell = toGlobalCells(toStoredTree(root, stored, root, null)).get(
    root
  );
  return toLayoutSize(rootCell?.colSpan ?? 1, rootCell?.rowSpan ?? 1);
};

export const buildBlocksContext = ({
  loadedGraph,
  groupingRules,
  i18nLocale,
  locale,
  arrangements,
  storedLayout,
}: BlocksContextArgs): BlocksContext => {
  const {
    graph: { root },
  } = buildBlocksGraph({
    loadedGraph,
    styleByModelId: NO_COMPONENT_STYLES,
    arrangements,
    collapsedGroupIds: [],
    locale,
    groupingRules,
    formattingFilters: NO_LABEL_RULES,
    formatUserFieldFn: NO_USER_FIELD_FORMATTING,
    i18nLocale,
  });
  const { containers, omitted } = describeContainers(
    loadedGraph.scopeData,
    root
  );
  return {
    containers,
    omittedContainerCount: omitted,
    appliedArrangements: resolveContainerArrangements({
      scopeData: loadedGraph.scopeData,
      root,
      locale,
      arrangements: effectiveArrangements(
        loadedGraph.scopeData,
        root,
        arrangements
      ),
    }).map(toAppliedArrangement),
    layoutSize:
      storedLayout && Object.keys(storedLayout).length
        ? measureStoredLayout(root, storedLayout)
        : measureLayout(root),
  };
};
